/**
 * Turning a volume slider's position into a playback level.
 *
 * Loudness is heard roughly logarithmically, so a slider whose position *is* the
 * gain puts its resolution in all the wrong places. The top half of a linear
 * slider, 50 to 100, covers six decibels - a barely noticeable change spread
 * over half the travel - while everything anyone would call quiet is crushed
 * into the bottom few percent. Down there the steps are enormous: 1, 2 and 3 are
 * about six, four and two decibels apart, so a feed you have turned down cannot
 * be adjusted in small amounts at all. That is the complaint this answers.
 *
 * So the slider is linear in decibels instead. Every step is then the same size
 * to the ear: a hundredth of the range, which is half a decibel across the fifty
 * decibels below unity. Fine at the bottom, where it was uselessly coarse, and
 * no longer absurdly fine at the top, where a linear slider's last step is under
 * a tenth of a decibel.
 *
 * MIN_DB is a floor rather than true silence, because a logarithmic scale never
 * reaches zero. Fifty decibels down is inaudible in practice, and position zero
 * is special-cased to a hard zero so the bottom of the slider still means off. A
 * deeper floor would only spend more of the travel on levels nobody can hear.
 *
 * What gets stored stays a gain, never a position. Levels are kept per person by
 * volumes.js and in the saved settings, they are multiplied together by
 * effectiveVolume() - gains multiply, positions do not - and they end up on
 * `element.volume`, which wants a gain. This is therefore a translation at the
 * edge, applied only where a slider is read or written, and nothing already
 * saved needs converting.
 */

/** How far below unity the bottom of a slider reaches, in decibels. */
export const MIN_DB = -50;

/**
 * The same, for the jukebox: the quietest thing YouTube's player can do.
 *
 * Its setVolume() takes whole numbers from 0 to 100 and applies them as a
 * linear gain, so the softest audible setting is 1 - a hundredth, or forty
 * decibels down. Spread over MIN_DB's fifty, the bottom of the jukebox slider
 * was a level YouTube cannot play: positions 1 to 7 rounded to silence, 8 to 25
 * all landed on 1, and the curve only began to move a third of the way up -
 * a dead zone and then a lurch, which is not what the other sliders feel like.
 * Ending the range where YouTube's does puts the whole travel on levels it can
 * actually produce.
 */
export const JUKEBOX_MIN_DB = -40;

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

/**
 * The gain for a slider position, where `position` is the slider's own 0..100.
 *
 * Position zero is silence; every position above it lands between the floor
 * and unity. Anything unusable - a missing value, a string that is not a
 * number - reads as silence rather than throwing, because the one caller is a
 * DOM value. `floorDb` is MIN_DB unless the output cannot reach that low.
 */
export function gainForSlider(position, floorDb = MIN_DB) {
  const pos = Number(position);
  if (!Number.isFinite(pos) || pos <= 0) return 0;
  return 10 ** ((floorDb * (1 - clamp(pos, 0, 100) / 100)) / 20);
}

/**
 * Where a gain sits on the slider, as the slider's own 0..100.
 *
 * The inverse of gainForSlider() to within the rounding, so a position read back
 * out of a level that came from a slider is the position that was set. Levels
 * quieter than the floor collapse to zero, which is the only place the pair is
 * deliberately lossy: they are all inaudible, and the alternative is a slider
 * that will not sit at its own bottom.
 */
export function sliderForGain(gain, floorDb = MIN_DB) {
  const value = Number(gain);
  if (!Number.isFinite(value) || value <= 0) return 0;
  const decibels = 20 * Math.log10(clamp(value, 0, 1));
  if (decibels <= floorDb) return 0;
  return Math.round((1 - decibels / floorDb) * 100);
}

export default { MIN_DB, JUKEBOX_MIN_DB, gainForSlider, sliderForGain };
